import { useQuery } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { getDecisionMakerOutreachStats } from './api';

const QUERY_KEY = 'contact-decision-maker-outreach-stats';

export type DecisionMakerOutreachStatsResult = {
	/** `undefined` until it has loaded — distinct from a real 0, which means nobody has reached out. */
	contactedSupporterCount: number | undefined;
	isLoading: boolean;
};

/**
 * How many supporters have messaged this petition's decision maker.
 *
 * A lazy query rather than pageData: it is read behind a modal on the success screen, and it changes
 * as other supporters send, so a server-cached page payload would go stale.
 *
 * KNOWN: the success screen and the SPH impact card both read this key seconds apart, so it is
 * fetched twice per session. Adding `staleTime` alone does not fix that — the app QueryClient sets
 * `refetchOnMount: 'always'`, which ignores stale time, and `gcTime: 0`, which drops the entry on
 * unmount so there is no cache to share. A real fix needs `refetchOnMount: false` here too, or one
 * shared owner for the query. Left alone until the second read shows up as a real cost.
 */
export function useDecisionMakerOutreachStats(petitionId: string): DecisionMakerOutreachStatsResult {
	const utilityContext = useUtilityContext();

	const { data, isLoading } = useQuery({
		queryKey: [QUERY_KEY, { id: petitionId }],
		queryFn: async () => getDecisionMakerOutreachStats({ id: petitionId, utilityContext }),
	});

	return { contactedSupporterCount: data ?? undefined, isLoading };
}
